import React, { useEffect, useMemo, useState } from 'react'

import Header from '_components/header'

import { getCustomAudienceSchema } from '_services/audience'

import StageStepper from './components/stage-stepper'
import QueryStage from './stages/query-stage'
import EditStage from './stages/edit-stage'
import ReviewStage from './stages/review-stage'
import RunStage from './stages/run-stage'
import { getCollection } from './collections'
import { buildPlan, createEmptyOperations, normalizeOperations } from './lib/operations'
import { createSession, saveSession, RESULT_STATUS } from './lib/session-store'

import './styles.scss'

const STAGES = [
  { id: 'query', label: 'Query' },
  { id: 'edit', label: 'Edit' },
  { id: 'review', label: 'Review' },
  { id: 'run', label: 'Run' },
]

const BulkEditAudiencesPage = () => {
  const [stage, setStage] = useState('query')
  const [collectionId, setCollectionId] = useState(null)
  const [audiences, setAudiences] = useState([])
  const [operations, setOperations] = useState(createEmptyOperations())
  const [session, setSession] = useState(null)
  const [schema, setSchema] = useState({})
  // audience ids the user removed from the run on the review stage
  const [excludedIds, setExcludedIds] = useState(() => new Set())

  const collection = getCollection(collectionId)

  useEffect(() => {
    // used for destination-specific field labels and enums (custom audiences)
    getCustomAudienceSchema()
      .then(setSchema)
      .catch(() => {})
  }, [])

  const plan = useMemo(() => buildPlan(audiences, operations, collection), [
    audiences,
    operations,
    collection,
  ])
  const changedItems = useMemo(
    () => plan.filter(({ audience, changeCount }) => changeCount > 0 && !excludedIds.has(audience._id)),
    [plan, excludedIds]
  )

  const toggleExcluded = (id) =>
    setExcludedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const handleLoaded = ({
    collection: loadedCollection,
    query,
    queryLabel,
    audiences: loaded,
    operations: previous,
  }) => {
    setCollectionId(loadedCollection.id)
    setAudiences(loaded)
    setExcludedIds(new Set())
    setSession(
      createSession({ collection: loadedCollection.id, query, queryLabel, audiences: loaded })
    )
    // restored sessions may predate current operation fields — normalise them
    setOperations(normalizeOperations(previous))
    setStage('edit')
  }

  const handleReview = () => {
    setSession(saveSession({ ...session, operations }))
    setStage('review')
  }

  const handleStartRun = () => {
    // flag the audiences that will not run, so the report is explicit about them
    const results = { ...session.results }
    plan.forEach(({ audience, changeCount }) => {
      if (changeCount === 0) {
        results[audience._id] = { ...results[audience._id], status: RESULT_STATUS.UNCHANGED }
      } else if (excludedIds.has(audience._id)) {
        results[audience._id] = { ...results[audience._id], status: RESULT_STATUS.EXCLUDED }
      }
    })
    setSession(saveSession({ ...session, operations, results }))
    setStage('run')
  }

  const handleNewBulkEdit = () => {
    setCollectionId(null)
    setAudiences([])
    setOperations(createEmptyOperations())
    setExcludedIds(new Set())
    setSession(null)
    setStage('query')
  }

  return (
    <div id="bulk-edit-audiences-page">
      <Header title="Bulk Edit Audiences" className="header__container" />

      <div className="bulk-edit">
        <div className="bulk-edit__notice">
          This tool queries, edits and updates audiences directly from your browser. Nothing runs on
          a server: keep this tab open while an update is in progress. Every run is logged locally
          so it can be audited and retried.
        </div>

        <StageStepper stages={STAGES} current={stage} />

        <Choose>
          <When condition={stage === 'query'}>
            <QueryStage onLoaded={handleLoaded} />
          </When>
          <When condition={stage === 'edit'}>
            <EditStage
              collection={collection}
              audiences={audiences}
              plan={plan}
              operations={operations}
              setOperations={setOperations}
              schema={schema}
              onBack={() => setStage('query')}
              onContinue={handleReview}
            />
          </When>
          <When condition={stage === 'review'}>
            <ReviewStage
              collection={collection}
              plan={plan}
              excludedIds={excludedIds}
              onToggleExclude={toggleExcluded}
              onClearExcluded={() => setExcludedIds(new Set())}
              onBack={() => setStage('edit')}
              onStart={handleStartRun}
            />
          </When>
          <When condition={stage === 'run'}>
            <RunStage
              key={session?.id}
              collection={collection}
              session={session}
              onSessionChange={setSession}
              items={changedItems}
              onNewBulkEdit={handleNewBulkEdit}
            />
          </When>
        </Choose>
      </div>
    </div>
  )
}

export default BulkEditAudiencesPage
